import { auth } from '../firebase/config';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

class ApiService {
  private baseUrl: string;

  constructor() {
    // In development, use local emulator. In production, use deployed functions
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? (process.env.REACT_APP_API_URL || 'https://api-q45ujgsaia-uc.a.run.app') + '/api'
      : '/api';
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (user) {
        return await user.getIdToken();
      }
      return null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  async makeRequest(endpoint: string, options: RequestInit = {}) {
    const token = await this.getAuthToken();
    
    console.log('API Request Debug:');
    console.log('  Endpoint:', endpoint);
    console.log('  Base URL:', this.baseUrl);
    console.log('  Full URL:', `${this.baseUrl}${endpoint}`);
    console.log('  Token available:', !!token);
    console.log('  Method:', options.method || 'GET');
    console.log('  Body:', options.body);
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    };

    console.log('  Headers:', config.headers);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    if (!response.ok) {
      console.log('API Response Error:');
      console.log('  Status:', response.status);
      console.log('  Status Text:', response.statusText);
      
      const errorData = await response.json().catch(() => ({}));
      console.log('  Error Data:', errorData);
      
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    } else {
      console.log('API Response Success:');
      console.log('  Status:', response.status);
      console.log('  Status Text:', response.statusText);
      
      const responseData = await response.json().catch(() => ({}));
      console.log('  Response Data:', responseData);
      
      return responseData;
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return { message: "Success" };
    }

    return response.json();
  }

  private async makeBinaryRequest(endpoint: string, options: RequestInit = {}) {
    const token = await this.getAuthToken();
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    };

    // Don't set Content-Type for FormData - let the browser set it with boundary
    if (options.body instanceof FormData && config.headers) {
      const headers = config.headers as Record<string, string>;
      delete headers['Content-Type'];
    }

    console.log('API: Making binary request to:', `${this.baseUrl}${endpoint}`);
    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    console.log('API: Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      console.error('API: Request failed with status:', response.status, response.statusText);
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('API: Error response data:', errorData);
        if (errorData.error) {
          errorMessage = errorData.error;
        }
        if (errorData.details) {
          errorMessage += ` - ${errorData.details}`;
        }
      } catch (e) {
        // If we can't parse the error response, use the status text
        console.warn('Could not parse error response:', e);
      }
      throw new Error(errorMessage);
    }

    console.log('API: Binary request successful');
    return response;
  }

  // Generic HTTP methods for external services
  async get(endpoint: string) {
    return this.makeRequest(endpoint);
  }

  async post(endpoint: string, data: any) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint: string, data: any) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint: string) {
    return this.makeRequest(endpoint, {
      method: 'DELETE',
    });
  }

  // Patient endpoints
  async getPatients(page: number = 1, limit: number = 10, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.makeRequest(`/patients?${params.toString()}`);
  }

  async getPatientById(id: string) {
    return this.makeRequest(`/patients/${id}`);
  }

  async createPatient(patientData: any) {
    return this.makeRequest('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  }

  async updatePatient(id: string, patientData: any) {
    return this.makeRequest(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    });
  }

  async deletePatient(id: string) {
    return this.makeRequest(`/patients/${id}`, {
      method: 'DELETE',
    });
  }

  // Order endpoints
  async getOrders(page: number = 1, limit: number = 10) {
    return this.makeRequest(`/orders?page=${page}&limit=${limit}`);
  }

  async getOrderById(id: string) {
    return this.makeRequest(`/orders/${id}`);
  }

  async createOrder(orderData: any) {
    return this.makeRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  async updateOrder(id: string, orderData: any) {
    return this.makeRequest(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    });
  }

  async updateOrderWithAttachments(id: string, formData: FormData) {
    const response = await this.makeBinaryRequest(`/orders/${id}`, {
      method: 'PUT',
      body: formData,
    });
    
    return response.json();
  }

  // Attachment endpoints
  async getOrderAttachments(orderId: string) {
    return this.makeRequest(`/attachments/order/${orderId}`);
  }

  async getPatientAttachments(patientId: string) {
    return this.makeRequest(`/attachments/patient/${patientId}`);
  }

  async getDownloadSignedUrl(storagePath: string) {
    try {
      const storageRef = ref(storage, storagePath);
      const downloadURL = await getDownloadURL(storageRef);
      return { success: true, downloadURL };
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  // Upload file directly to Firebase Storage using Firebase SDK
  async uploadFileDirectly(file: File, metadata: any) {
    try {
      console.log('Uploading file directly to Firebase Storage:', file.name);
      
      // Create a storage reference
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${metadata.orderId}_${timestamp}.${fileExtension}`;
      const storagePath = `orders/${metadata.orderId}/${fileName}`;
      
      const storageRef = ref(storage, storagePath);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          uploadedBy: metadata.uploadedBy,
          orderId: metadata.orderId,
          patientId: metadata.patientId,
          includeInReport: metadata.includeInReport
        }
      });
      
      console.log('File uploaded successfully:', storagePath);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return { 
        success: true,
        storagePath,
        fileName,
        downloadURL
      };
    } catch (error) {
      console.error('Error uploading file to Firebase Storage:', error);
      throw error;
    }
  }

  // Upload file using backend endpoint (fallback when signed URLs don't work)
  async uploadFileViaBackend(file: File, metadata: any) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add metadata as form fields
    Object.keys(metadata).forEach(key => {
      formData.append(key, metadata[key]);
    });

    const response = await this.makeBinaryRequest('/attachments/upload', {
      method: 'POST',
      body: formData,
    });

    return response.json();
  }

  // Create attachment record in database after successful upload
  async createAttachmentRecord(attachmentData: any) {
    return this.makeRequest('/attachments/record', {
      method: 'POST',
      body: JSON.stringify(attachmentData),
    });
  }

  async updateAttachment(id: string, data: { includeInReport?: boolean; fileName?: string }) {
    return this.makeRequest(`/attachments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAttachment(id: string) {
    return this.makeRequest(`/attachments/${id}`, {
      method: 'DELETE',
    });
  }

  async downloadAttachment(id: string) {
    return this.makeBinaryRequest(`/attachments/download/${id}`, {
      method: 'GET',
    });
  }

  async deleteOrder(id: string) {
    return this.makeRequest(`/orders/${id}`, {
      method: 'DELETE',
    });
  }

  async getUniqueAccessions(page: number = 1, limit: number = 10, search?: string, status?: string) {
    let url = `/orders/unique-accessions?page=${page}&limit=${limit}`;
    if (search && search.trim() !== '') {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (status && status.trim() !== '') {
      url += `&status=${encodeURIComponent(status)}`;
    }
    return this.makeRequest(url);
  }

  async getOrderStatusCounts() {
    return this.makeRequest('/orders/status-counts');
  }

  async getMyTasks(page: number = 1, limit: number = 10) {
    return this.makeRequest(`/orders/my-tasks?page=${page}&limit=${limit}`);
  }

  async getOrderVersions(accessionId: string) {
    return this.makeRequest(`/orders/${accessionId}/versions`);
  }

  // Amendment-related methods
  async getAmendmentHistory(orderId: string) {
    console.log('API: Getting amendment history for order:', orderId);
    return this.makeRequest(`/orders/${orderId}/amendment-history`);
  }

  async getOriginalOrder(amendedFromId: string) {
    console.log('API: Getting original order for amendedFromId:', amendedFromId);
    return this.makeRequest(`/orders/${amendedFromId}/original-order`);
  }

  async getAmendmentChain(accessionId: string) {
    console.log('API: Getting amendment chain for accession:', accessionId);
    return this.makeRequest(`/orders/${accessionId}/amendment-chain`);
  }

  async createAddendum(addendumData: any) {
    return this.makeRequest('/addenda', {
      method: 'POST',
      body: JSON.stringify(addendumData),
    });
  }

  // Specimen endpoints
  async getSpecimens(page: number = 1, limit: number = 10) {
    return this.makeRequest(`/specimens?page=${page}&limit=${limit}`);
  }

  async getSpecimenById(id: string) {
    return this.makeRequest(`/specimens/${id}`);
  }

  async getSpecimenParts() {
    return this.makeRequest('/specimens/specimen-parts');
  }

  async createSpecimen(specimenData: any) {
    return this.makeRequest('/specimens', {
      method: 'POST',
      body: JSON.stringify(specimenData),
    });
  }

  async updateSpecimen(id: string, specimenData: any) {
    return this.makeRequest(`/specimens/${id}`, {
      method: 'PUT',
      body: JSON.stringify(specimenData),
    });
  }

  async deleteSpecimen(id: string) {
    return this.makeRequest(`/specimens/${id}`, {
      method: 'DELETE',
    });
  }

  // User endpoints
  async getUsers(page: number = 1, limit: number = 10) {
    return this.makeRequest(`/users?page=${page}&limit=${limit}`);
  }

  async getUserById(id: string) {
    return this.makeRequest(`/users/${id}`);
  }

  async createUser(userData: any) {
    // For new user creation, we'll use Firebase Auth directly
    // The Firebase Auth trigger will automatically create the PostgreSQL record
    throw new Error('Use createUserWithFirebaseAuth instead for new user creation');
  }

  async createUserWithFirebaseAuth(userData: any) {
    // This method should be called from the frontend to create users via Firebase Auth
    // The actual implementation will be in the Users component
    throw new Error('This method should be implemented in the Users component using Firebase Auth directly');
  }

  async updateUser(id: string, userData: any) {
    return this.makeRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id: string) {
    return this.makeRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  }



  async getUserMe() {
    return this.makeRequest('/users/me');
  }

  async resetUserPassword(id: string, password: string) {
    return this.makeRequest(`/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.makeRequest('/users/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }



  // Role endpoints
  async getRoles(page: number = 1, limit: number = 10, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.makeRequest(`/roles?${params.toString()}`);
  }

  async getRoleById(id: string) {
    return this.makeRequest(`/roles/${id}`);
  }

  async createRole(roleData: any) {
    return this.makeRequest('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  }

  async updateRole(id: string, roleData: any) {
    return this.makeRequest(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
  }

  async deleteRole(id: string) {
    return this.makeRequest(`/roles/${id}`, {
      method: 'DELETE',
    });
  }

  // Location endpoints
  async getLocations() {
    return this.makeRequest('/locations');
  }

  async getLocationsHierarchy() {
    return this.makeRequest('/locations');
  }

  async getAllLocations() {
    return this.makeRequest('/locations/all');
  }

  async getLocation(id: string) {
    return this.makeRequest(`/locations/${id}`);
  }

  async createLocation(locationData: any) {
    return this.makeRequest('/locations', {
      method: 'POST',
      body: JSON.stringify(locationData),
    });
  }

  async updateLocation(id: string, locationData: any) {
    return this.makeRequest(`/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(locationData),
    });
  }

  async deleteLocation(id: string) {
    return this.makeRequest(`/locations/${id}`, {
      method: 'DELETE',
    });
  }

  async updateLocationBranding(id: string, branding: any) {
    return this.makeRequest(`/locations/${id}/branding`, {
      method: 'PUT',
      body: JSON.stringify(branding),
    });
  }

  // Report endpoints
  async getReports(page: number = 1, limit: number = 10) {
    return this.makeRequest(`/reports?page=${page}&limit=${limit}`);
  }

  async createReport(reportData: any) {
    return this.makeRequest('/reports', {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
  }

  async updateReport(id: string, reportData: any) {
    return this.makeRequest(`/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reportData),
    });
  }

  // Macro endpoints
  async getMacros(page: number = 1, limit: number = 10) {
    return this.makeRequest(`/macros?page=${page}&limit=${limit}`);
  }

  async getSpecimenClassSections() {
    return this.makeRequest('/specimen-class-sections');
  }

  async getSpecimenClassSectionById(sectionId: string) {
    const response = await this.makeRequest(`/specimen-classes/sections/${sectionId}`);
    // The backend now returns the section directly with parsed fields
    return response;
  }

  async createMacro(macroData: any) {
    return this.makeRequest('/macros', {
      method: 'POST',
      body: JSON.stringify(macroData),
    });
  }

  async updateMacro(id: string, macroData: any) {
    return this.makeRequest(`/macros/${id}`, {
      method: 'PUT',
      body: JSON.stringify(macroData),
    });
  }

  async deleteMacro(id: string) {
    return this.makeRequest(`/macros/${id}`, {
      method: 'DELETE',
    });
  }

  // ICD Code endpoints
  async getIcdCodes(page: number = 1, limit: number = 20, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.makeRequest(`/codes/icd?${params.toString()}`);
  }

  async createIcdCode(icdData: any) {
    return this.makeRequest('/codes/icd', {
      method: 'POST',
      body: JSON.stringify(icdData),
    });
  }

  async updateIcdCode(code: string, icdData: any) {
    return this.makeRequest(`/codes/icd/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: JSON.stringify(icdData),
    });
  }

  async deleteIcdCode(code: string) {
    return this.makeRequest(`/codes/icd/${encodeURIComponent(code)}`, {
      method: 'DELETE',
    });
  }

  // SNOMED Code endpoints
  async getSnomedCodes(page: number = 1, limit: number = 20, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.makeRequest(`/codes/snomed?${params.toString()}`);
  }

  async createSnomedCode(snomedData: any) {
    return this.makeRequest('/codes/snomed', {
      method: 'POST',
      body: JSON.stringify(snomedData),
    });
  }

  async updateSnomedCode(code: string, snomedData: any) {
    return this.makeRequest(`/codes/snomed/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: JSON.stringify(snomedData),
    });
  }

  async deleteSnomedCode(code: string) {
    return this.makeRequest(`/codes/snomed/${encodeURIComponent(code)}`, {
      method: 'DELETE',
    });
  }

  // Staff endpoints
  async getPathologists() {
    return this.makeRequest('/staff/pathologists');
  }

  async getGrossingTechs() {
    return this.makeRequest('/staff/grossing-techs');
  }

  async getAccessioners() {
    return this.makeRequest('/staff/accessioners');
  }

  async getCytotechnologists() {
    return this.makeRequest('/staff/cytotechnologists');
  }

  // Order parts endpoints
  async getOrderParts(orderId: string) {
    return this.makeRequest(`/orders/${orderId}/parts`);
  }

  // Histology endpoints
  async getHistology(orderId: string, specimenPartId: string, partCode: string) {
    const params = new URLSearchParams({
      order_id: orderId,
      specimen_part_id: specimenPartId,
      part_code: partCode
    });
    return this.makeRequest(`/histology?${params}`);
  }

  async createHistology(histologyData: any) {
    return this.makeRequest('/histology', {
      method: 'POST',
      body: JSON.stringify(histologyData),
    });
  }

  async updateHistology(id: string, histologyData: any) {
    return this.makeRequest(`/histology/${id}`, {
      method: 'PUT',
      body: JSON.stringify(histologyData),
    });
  }

  async deleteHistology(id: string) {
    return this.makeRequest(`/histology/${id}`, {
      method: 'DELETE',
    });
  }

  // Specimen classes endpoints
  async getSpecimenClasses() {
    return this.makeRequest('/specimen-classes');
  }

  async getSpecimenClassById(id: string) {
    return this.makeRequest(`/specimen-classes/${id}`);
  }

  async getSpecimenClassParts(classId: string) {
    return this.makeRequest(`/specimen-classes/${classId}/parts`);
  }

  async getSpecimenClassGroups() {
    return this.makeRequest('/specimen-classes/groups');
  }

  async createSpecimenClass(specimenClassData: any) {
    return this.makeRequest('/specimen-classes', {
      method: 'POST',
      body: JSON.stringify(specimenClassData),
    });
  }

  async updateSpecimenClass(id: string, specimenClassData: any) {
    return this.makeRequest(`/specimen-classes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(specimenClassData),
    });
  }

  async deleteSpecimenClass(id: string) {
    return this.makeRequest(`/specimen-classes/${id}`, {
      method: 'DELETE',
    });
  }

  // AP case templates endpoints
  async getApCaseTemplatesForSpecimenClass(specimenClassId: string) {
    return this.makeRequest(`/ap-case-templates/for-specimen-class/${specimenClassId}`);
  }

  // Addenda endpoints
  async getAddenda(reportId?: string, ids?: string[]) {
    const params = new URLSearchParams();
    if (reportId) params.append('report_id', reportId);
    if (ids && ids.length > 0) params.append('ids', ids.join(','));
    return this.makeRequest(`/addenda?${params}`);
  }

  async updateAddendum(id: string, addendumData: any) {
    return this.makeRequest(`/addenda/${id}`, {
      method: 'PUT',
      body: JSON.stringify(addendumData),
    });
  }

  async deleteAddendum(id: string) {
    return this.makeRequest(`/addenda/${id}`, {
      method: 'DELETE',
    });
  }

  // CPT codes endpoints
  async getCptCodes(page: number = 1, limit: number = 20, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.makeRequest(`/codes/cpt?${params.toString()}`);
  }

  async createCptCode(cptData: any) {
    return this.makeRequest('/codes/cpt', {
      method: 'POST',
      body: JSON.stringify(cptData),
    });
  }

  async updateCptCode(id: string, cptData: any) {
    return this.makeRequest(`/codes/cpt/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(cptData),
    });
  }

  async deleteCptCode(id: string) {
    return this.makeRequest(`/codes/cpt/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async getCptCodesByPart(partId: string) {
    return this.makeRequest(`/specimens/specimen-parts-codes-mapping?specimen_parts_id=${partId}&code_type=CPT`);
  }

  // Modifiers endpoints
  async getModifiers(page: number = 1, limit: number = 20, search?: string, category?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    if (category) {
      params.append('category', category);
    }
    
    return this.makeRequest(`/codes/modifiers?${params.toString()}`);
  }

  async createModifier(modifierData: any) {
    return this.makeRequest('/codes/modifiers', {
      method: 'POST',
      body: JSON.stringify(modifierData),
    });
  }

  async updateModifier(id: string, modifierData: any) {
    return this.makeRequest(`/codes/modifiers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(modifierData),
    });
  }

  async deleteModifier(id: string) {
    return this.makeRequest(`/codes/modifiers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // Specimen parts codes mapping endpoints
  async getSpecimenPartsCodesMapping(page: number = 1, limit: number = 20, specimenPartId?: string, codeType?: string) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (specimenPartId) params.append('specimen_parts_id', specimenPartId);
    if (codeType) params.append('code_type', codeType);
    return this.makeRequest(`/specimens/specimen-parts-codes-mapping?${params}`);
  }

  async createSpecimenPartsCodesMapping(mappingData: any) {
    return this.makeRequest('/specimens/specimen-parts-codes-mapping', {
      method: 'POST',
      body: JSON.stringify(mappingData),
    });
  }

  async updateSpecimenPartsCodesMapping(id: string, mappingData: any) {
    return this.makeRequest(`/specimens/specimen-parts-codes-mapping/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(mappingData),
    });
  }

  async deleteSpecimenPartsCodesMapping(id: string) {
    return this.makeRequest(`/specimens/specimen-parts-codes-mapping/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // Diagnosis terms ICD code mapping endpoints
  async getDiagnosisTermsIcdCodeMapping(page: number = 1, limit: number = 20) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return this.makeRequest(`/diagnosis-terms-icdcode-mapping?${params}`);
  }

  async createDiagnosisTermsIcdCodeMapping(mappingData: any) {
    return this.makeRequest('/diagnosis-terms-icdcode-mapping', {
      method: 'POST',
      body: JSON.stringify(mappingData),
    });
  }

  async updateDiagnosisTermsIcdCodeMapping(id: string, mappingData: any) {
    return this.makeRequest(`/diagnosis-terms-icdcode-mapping/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(mappingData),
    });
  }

  async deleteDiagnosisTermsIcdCodeMapping(id: string) {
    return this.makeRequest(`/diagnosis-terms-icdcode-mapping/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // Order specimen parts endpoints
  async getOrderSpecimenParts(orderId: string) {
    return this.makeRequest(`/specimens/order_specimen_parts?order_id=${orderId}`);
  }

  async createOrderSpecimenPart(partData: any) {
    return this.makeRequest('/specimens/order_specimen_parts', {
      method: 'POST',
      body: JSON.stringify(partData),
    });
  }

  async updateOrderSpecimenPart(id: string, partData: any) {
    return this.makeRequest(`/specimens/order_specimen_parts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(partData),
    });
  }

  async deleteOrderSpecimenPart(id: string) {
    return this.makeRequest(`/specimens/order_specimen_parts/${id}`, {
      method: 'DELETE',
    });
  }

  // PDF generation endpoint - always get latest version
  async generateOrderPDF(accessionId: string) {
    console.log('API: Generating PDF for latest version of accession:', accessionId);
    const response = await this.makeBinaryRequest(`/pdf/orders/accession/${accessionId}/latest`);
    console.log('API: PDF request successful, response status:', response.status);
    return response;
  }

  // Communication endpoints
  async sendEmail(request: any) {
    return this.makeRequest('/sendCommunication/email', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async sendSMS(request: any) {
    return this.makeRequest('/sendCommunication/sms', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async sendFax(request: any) {
    return this.makeRequest('/sendCommunication/fax', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Partner management endpoints
  async getPartners(page: number = 1, limit: number = 10, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.makeRequest(`/partners?${params.toString()}`);
  }

  async getPartnerById(id: string) {
    return this.makeRequest(`/partners/${id}`);
  }

  async createPartner(partnerData: any) {
    return this.makeRequest('/partners', {
      method: 'POST',
      body: JSON.stringify(partnerData),
    });
  }

  async updatePartner(id: string, partnerData: any) {
    return this.makeRequest(`/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(partnerData),
    });
  }

  async deletePartner(id: string) {
    return this.makeRequest(`/partners/${id}`, {
      method: 'DELETE',
    });
  }

  // API Key management endpoints
  async getApiKeys(partnerId?: string) {
    const params = partnerId ? `?partnerId=${partnerId}` : '';
    return this.makeRequest(`/api-keys${params}`);
  }

  async getApiKeyById(id: string) {
    return this.makeRequest(`/api-keys/${id}`);
  }

  async createApiKey(apiKeyData: any) {
    return this.makeRequest('/api-keys', {
      method: 'POST',
      body: JSON.stringify(apiKeyData),
    });
  }

  async updateApiKey(id: string, apiKeyData: any) {
    return this.makeRequest(`/api-keys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(apiKeyData),
    });
  }

  async deleteApiKey(id: string) {
    return this.makeRequest(`/api-keys/${id}`, {
      method: 'DELETE',
    });
  }

  async regenerateApiKey(id: string) {
    return this.makeRequest(`/api-keys/${id}/regenerate`, {
      method: 'POST',
    });
  }

  // SFTP SSH Key management endpoints
  async getSftpSshKeys(partnerId?: string) {
    const params = partnerId ? `?partnerId=${partnerId}` : '';
    return this.makeRequest(`/sftp-ssh-keys${params}`);
  }

  async getSftpSshKeyById(id: string) {
    return this.makeRequest(`/sftp-ssh-keys/${id}`);
  }

  async createSftpSshKey(sftpSshKeyData: any) {
    return this.makeRequest('/sftp-ssh-keys', {
      method: 'POST',
      body: JSON.stringify(sftpSshKeyData),
    });
  }

  async updateSftpSshKey(id: string, sftpSshKeyData: any) {
    return this.makeRequest(`/sftp-ssh-keys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(sftpSshKeyData),
    });
  }

  async deleteSftpSshKey(id: string) {
    return this.makeRequest(`/sftp-ssh-keys/${id}`, {
      method: 'DELETE',
    });
  }

  // Insurance endpoints
  async getPatientInsurances(patientId: string) {
    return this.makeRequest(`/patients/${patientId}/insurances`);
  }

  async getInsuranceById(id: string) {
    return this.makeRequest(`/insurances/${id}`);
  }

  async createInsurance(insuranceData: any) {
    return this.makeRequest('/insurances', {
      method: 'POST',
      body: JSON.stringify(insuranceData),
    });
  }

  async updateInsurance(id: string, insuranceData: any) {
    return this.makeRequest(`/insurances/${id}`, {
      method: 'PUT',
      body: JSON.stringify(insuranceData),
    });
  }

  async deleteInsurance(id: string) {
    return this.makeRequest(`/insurances/${id}`, {
      method: 'DELETE',
    });
  }

  async setInsuranceAsPrimary(id: string, patientId: string) {
    return this.makeRequest(`/insurances/${id}/set-primary`, {
      method: 'PUT',
      body: JSON.stringify({ patientId }),
    });
  }
}

const apiService = new ApiService();
export default apiService; 